import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Repeat } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import { useTherapist } from "../../../../context/useTherapist";
import { saveDefaultPaymentLine } from "../../application/defaultPaymentLine";

/**
 * The line a new payment starts with.
 *
 * Most therapists charge the same thing for the same session, over and over,
 * and retyping it every time is the sort of friction that makes a tool feel
 * like paperwork. Set once here, it pre-fills the first line on the create
 * form — where it stays fully editable, because a default that cannot be
 * overridden is a constraint, not a convenience.
 *
 * The two fields are independent: a therapist who varies the price but never
 * the wording still gets the concept filled in. Clearing a field and saving
 * removes the default rather than being ignored.
 */
const DefaultPaymentLineCard: React.FC = () => {
    const { t } = useTranslation();
    const { therapist, refreshTherapist } = useTherapist();

    const [concept, setConcept] = useState("");
    const [amount, setAmount] = useState("");
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!therapist) return;
        setConcept(therapist.default_payment_concept ?? "");
        setAmount(therapist.default_payment_amount ?? "");
    }, [therapist]);

    if (!therapist) return null;

    const save = async () => {
        const trimmed = concept.trim();
        const parsed = amount.trim() === "" ? null : Number(amount);

        if (parsed !== null && (Number.isNaN(parsed) || parsed < 0)) {
            setError(t("payments.settings.defaults.amountInvalid"));
            return;
        }

        setSaving(true);
        setError(null);
        setSaved(false);
        try {
            await saveDefaultPaymentLine(therapist.id, {
                default_payment_concept: trimmed === "" ? null : trimmed,
                // Sent as a string so a price never round-trips through a
                // float on its way to a decimal column.
                default_payment_amount: parsed === null ? null : parsed.toFixed(2),
            });
            await refreshTherapist?.();
            setSaved(true);
        } catch {
            setError(t("payments.settings.saveError"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="rounded-2xl border border-gray-200 p-5 dark:border-gray-700">
            <div className="mb-4 flex items-center gap-3">
                <Repeat className="text-content-tertiary" size={20} />
                <div>
                    <h3 className="text-base font-semibold text-content-primary">
                        {t("payments.settings.defaults.title")}
                    </h3>
                    <p className="text-sm text-content-tertiary">
                        {t("payments.settings.defaults.description")}
                    </p>
                </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-[1fr_10rem]">
                <div>
                    <label
                        htmlFor="default-payment-concept"
                        className="mb-1.5 block text-sm font-medium text-content-secondary"
                    >
                        {t("payments.settings.defaults.concept")}
                    </label>
                    <input
                        id="default-payment-concept"
                        type="text"
                        maxLength={500}
                        value={concept}
                        onChange={(e) => setConcept(e.target.value)}
                        placeholder={t("payments.settings.defaults.conceptPlaceholder")}
                        className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                    />
                </div>
                <div>
                    <label
                        htmlFor="default-payment-amount"
                        className="mb-1.5 block text-sm font-medium text-content-secondary"
                    >
                        {t("payments.settings.defaults.amount")}
                    </label>
                    <input
                        id="default-payment-amount"
                        type="number"
                        min="0"
                        step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                        className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                    />
                </div>
            </div>

            <p className="mt-3 text-xs text-content-tertiary">
                {t("payments.settings.defaults.hint")}
            </p>

            {error && (
                <p role="alert" className="mt-3 text-sm text-red-600">
                    {error}
                </p>
            )}
            {saved && !error && (
                <p role="status" className="mt-3 text-sm text-green-600">
                    {t("common.saved")}
                </p>
            )}

            <div className="mt-4 flex justify-end">
                <Button size="sm" loading={saving} onClick={save}>
                    {t("common.save")}
                </Button>
            </div>
        </div>
    );
};

export default DefaultPaymentLineCard;
